import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useGuideDetail } from "./hooks/useGuideDetail";
import BannerCarousel from "./components/BannerCarousel";
import TagRow from "./components/TagRow";
import StatsBar from "./components/StatsBar";
import IntroSection from "./components/IntroSection";
import WorkInfo from "./components/WorkInfo";
import ServiceItems from "./components/ServiceItems";
import Lightbox, { PhotoGallery } from "./components/Lightbox";
import { THEME } from "./constants";
import { resolveDetailId } from "@/utils/detailNav";

export default function GuideDetailProductPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // ★ 同时支持 URL query（?guideId=x）与 location.state
  //   注意：此前用 `|| 1` 兜底，拿不到 ID 时会静默展示 1 号达人，属严重误导，已移除
  const guideId = resolveDetailId("guideId", searchParams, location.state);

  return <GuideDetailContent guideId={guideId} navigate={navigate} />;
}

/** 有 guideId 时正常渲染详情（拆出子组件以保证 guideId 为 number 类型） */
function GuideDetailContent({
  guideId,
  navigate,
}: {
  guideId: number | undefined;
  navigate: ReturnType<typeof useNavigate>;
}) {
  // ★ 无有效 ID 时直接提示，避免发起无效请求或展示错误商品
  const missingId = guideId === undefined;

  // 数据获取（guideId 缺失时传 0，hook 内部会请求失败并进入错误态）
  const { guide, loading, error, serviceItems } = useGuideDetail({
    guideId: guideId ?? 0,
  });

  // 相册全屏预览
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  /** 加载中 */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#1A1D26" }}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-white/20 border-t-[#FF6B35] rounded-full animate-spin" />
          <span className="text-sm" style={{ color: "rgba(255,255,255,0.5)" }}>加载中...</span>
        </div>
      </div>
    );
  }

  /** 错误 / 无数据 */
  if (missingId || error || !guide) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#1A1D26" }}>
        <div className="flex flex-col items-center gap-4 px-8">
          <i className="ri-error-warning-line" style={{ fontSize: 48, color: "rgba(255,107,53,0.6)" }} />
          <span className="text-sm text-center" style={{ color: "rgba(255,255,255,0.5)" }}>
            {missingId ? "缺少达人信息，请从列表重新进入" : error || "未找到达人信息"}
          </span>
          <button onClick={() => navigate(-1)} className="px-6 py-2.5 rounded-full text-sm font-medium cursor-pointer" style={{ background: THEME.primary, color: "#FFF" }}>
            返回列表
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative pb-[calc(env(safe-area-inset-bottom,0px)+112px)]" style={{ background: "#1A1D26", fontFamily: "'Inter','SF Pro Text',sans-serif" }}>
      {/* Banner 轮播 + 头像 */}
      <BannerCarousel guide={guide} onNavigateBack={() => navigate(-1)} />

      {/* 接单状态 + 评分 + 认证标签 */}
      <TagRow guide={guide} />

      {/* 数据统计栏 */}
      <StatsBar guide={guide} />

      {/* 服务标签 */}
      {guide.serviceLabel.length > 0 && (
        <div className="mx-4 mt-3 flex flex-wrap items-center gap-2">
          {guide.serviceLabel.map((tag, idx) => (
            <span key={idx} className="px-2.5 py-1 rounded-full text-xs font-medium"
              style={{ color: THEME.primary, background: `${THEME.primary}12`, border: `1px solid ${THEME.primaryBorder}` }}
            >{tag}</span>
          ))}
        </div>
      )}

      {/* 自我介绍 */}
      <IntroSection guide={guide} />

      {/* 工作时间 & 地址 */}
      <WorkInfo guide={guide} />

      {/* 用户评价入口 */}
      <div
        className="mx-4 mt-4 px-4 py-3.5 rounded-2xl flex items-center justify-between"
        style={{ background: "rgba(255,255,255,0.05)" }}
        onClick={() => navigate("/guide-comment", { state: { staffId: guide.id } })}
      >
        <h2 className="text-sm font-semibold text-white">用户评价</h2>
        <div className="flex items-center gap-1 cursor-pointer">
          <span className="text-xs" style={{ color: "rgba(255,255,255,0.45)" }}>{guide.commentCount || 0}点评</span>
          <i className="ri-arrow-right-s-line" style={{ fontSize: 14, color: "rgba(255,255,255,0.45)" }} />
        </div>
      </div>

      {/* 相册展示 */}
      <PhotoGallery guide={guide} onOpen={setLightboxIndex} />

      {/* 服务项目 */}
      <ServiceItems guide={guide} items={serviceItems} />

      {/* 全屏相册预览 */}
      <Lightbox guide={guide} index={lightboxIndex} onIndexChange={setLightboxIndex} />
    </div>
  );
}
